// ContactTable.tsx
import React, { useEffect, useState } from "react";
import ReusableTable from "../../../../../../global components/Table/Table";
import {
  PaginatedTeamResponse,
  Roles,
} from "../../../../../../libs/Utils/Types/Teams";
import useSWR from "swr";
import {
  parseClientError,
  swrFetcher,
} from "../../../../../../libs/api/Endpoints";
import { UseAuthToken } from "../../../../../../libs/api/userSession";
import NoData from "../../../../../../global components/Table/NoData/NoData";
import Elipses from "../../../../../../global components/Icons/Elipses";
import Paginate from "../../../../../../global components/Paginate/Pagination";
import SearchInput from "../../../../../../global components/Forms/SearchInput/SearchInput";
import s from "./Table.module.scss";
import ReusablePopover from "../../../../../../global components/Popover/ReuseablePopover";
import PopOverItems from "./PopOverItems/PopOverItems";
import clsx from "clsx";
import SelectField from "../../../../../../global components/Forms/SelectField/SelectField";
import Users from "../../../../../../global components/Icons/Users";
import Unauthorised from "../../../../../../global components/Unauthorised";

function Table() {
  const token = UseAuthToken();
  const [filter, setFilter] = useState<string>("");

  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageItems, setPageItems] = useState(50);
  const [teamUnauthorized, setTeamUnauthorized] = useState<
    boolean | { title: string; message: string }
  >(false);
  const { data: roles } = useSWR<{ data: Roles[] }, any, any>(
    token ? ["api/admin/team/roles", token] : null,
    swrFetcher,
    {
      revalidateOnFocus: true,
      shouldRetryOnError: false, // Disable retrying on errors
    },
  );
  const {
    data: TableContacts,
    isLoading,
    error,
    mutate,
  } = useSWR<PaginatedTeamResponse, any, any>(
    token
      ? [
          "api/admin/team/members",
          token,
          {
            page: currentPage,
            limit: pageItems,
            search: searchValue,
            ...(filter && { roleId: filter }),
          },
        ]
      : null,
    swrFetcher,
    {
      revalidateOnFocus: true,
      shouldRetryOnError: false, // Disable retrying on errors
    },
  );

  const handleSearch = (value: string) => {
    setSearchValue(value);
    // You can add your search logic here, e.g., fetching data based on the search term
  };
  const handlePageChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageItems(size);
  };
  // Define table columns
  const columns = [
    {
      title: "NAME",
      dataIndex: "name",
      key: "name",
      render: (name: string) => (
        <span className={clsx(s.cell, s.name)}>{name}</span>
      ),
    },
    {
      title: "EMAIL ADDRESS",
      dataIndex: "email",
      key: "email",
      render: (email: string) => <span className={clsx(s.cell)}>{email}</span>,
    },
    {
      title: "ROLE",
      dataIndex: "role",
      key: "role",
      render: (role: string) => <span className={clsx(s.cell)}>{role}</span>,
    },

    {
      title: "ACTIONS",
      dataIndex: "actions",
      key: "actions",
      render: (text: any, record: { key: string }) => (
        <ReusablePopover
          trigger={"click"}
          placement="leftBottom"
          content={<PopOverItems mutate={mutate} keyValue={record.key} />} // Pass the key value
        >
          <Elipses height={24} width={18} />
        </ReusablePopover>
      ),
    },
  ];
  // Transform API response to table data
  const transformedData = TableContacts?.data.map((contact) => ({
    key: contact.id,
    name: `${
      contact.firstName || contact.lastName
        ? (contact.firstName || "") + " " + (contact.lastName || "")
        : "N/A"
    }`,
    email: contact.email || "N/A",
    role: contact.role.title || "N/A",
    actions: "", // You can customize actions here as needed
  }));

  // Handle error state

  useEffect(() => {
    if (error) {
      const e = parseClientError(error);

      if (e.statusCode === 403) {
        setTeamUnauthorized(true);
      } else {
        setTeamUnauthorized({
          title: "Error!",
          message: e.message || "Something went wrong",
        });
      }
    }
  }, [error]);
  function handleFilter(value: string) {
    setFilter(value);
  }

  return (
    <div className={s.table}>
      <div className={s.searchParams}>
        <div className={s.params}>
          <div className={s.input}>
            <SearchInput placeholder="search" handleSearch={handleSearch} />
          </div>
          <SelectField
            bordered
            defaultValue={""}
            label={"Role:"}
            wrapperClassname={s.SelectWrapper}
            onChange={(value: any) => handleFilter(value)}
            className={s.selectInput}
            style={{ width: "100%", height: "100%" }}
            options={[
              { value: "", label: "All" },
              ...(roles?.data.map((role) => ({
                value: role.id,
                label: role.title,
              })) ?? []), // Use default empty array if roles is undefined
            ]}
          />
        </div>
        <div className={s.paginate}>
          <Paginate
            onChange={handlePageChange}
            total={TableContacts?.pagination.totalItems || 0}
            pageSize={pageItems}
          />
        </div>
      </div>
      <ReusableTable
        loading={isLoading}
        noDataComponent={
          teamUnauthorized ? (
            typeof teamUnauthorized === "boolean" ? (
              <Unauthorised />
            ) : (
              <Unauthorised error={teamUnauthorized} />
            )
          ) : (
            <NoData
              Message="No team yet"
              subMessage=""
              icon={<Users fill="#010516" height={80} width={80} />}
            />
          )
        }
        dataSource={transformedData || []}
        columns={columns}
        //rowSelection={rowSelection} // Include row selection prop
      />
    </div>
  );
}

export default Table;
